import axios from "axios";

// Resolve battery API base. Prefer explicit `VITE_BATTERY_API_URL`,
// then derive from `VITE_VEHICLE_API_URL` or `VITE_API_BASE_URL`, otherwise
// fall back to the vehicle-service battery mount used by the backend: `/api/v1/batteries`.
const BATTERY_API_URL =
  (import.meta.env.VITE_BATTERY_API_URL as string) ||
  (() => {
    const vVehicle = import.meta.env.VITE_VEHICLE_API_URL as string;
    const vBase = (import.meta.env.VITE_API_BASE_URL as string) || "";

    // If a vehicle API URL is provided, attempt to derive the batteries path
    if (vVehicle) {
      // Replace trailing `/vehicles` with `/v1/batteries` when present
      if (vVehicle.includes("/vehicles")) {
        return vVehicle.replace(/\/vehicles\/?$/, "/v1/batteries");
      }
      // Otherwise assume it's a host/root and append the service path
      return vVehicle.replace(/\/$/, "") + "/v1/batteries";
    }

    if (vBase) return vBase.replace(/\/$/, "") + "/v1/batteries";

    return "/api/v1/batteries";
  })();

// Helpful debug when running in browser/devtools so you can see
// which base URL the client resolved to.
console.debug('[batteryService] BATTERY_API_URL ->', BATTERY_API_URL);

// Battery configuration and status enums
export enum BatteryConfiguration {
  SINGLE = "Single",
  DUAL = "Dual",
}

export enum BatteryStatus {
  ACTIVE = "ACTIVE",
  IN_STORAGE = "IN_STORAGE",
  UNDER_REPAIR = "UNDER_REPAIR",
  DAMAGED = "DAMAGED",
  RETIRED = "RETIRED",
}

// Battery interfaces
export interface Battery {
  id: string;
  batteryNumber: string;
  parentVehicleId: string;
  currentVehicleId?: string;
  manufacturer?: string;
  batteryType?: string;
  capacity?: number;
  voltage?: number;
  healthPercentage?: number;
  cycleCount: number;
  lastChargeDate?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status: BatteryStatus;
  slotPosition?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  parentVehicle?: {
    id: string;
    registrationNumber: string;
    model?: {
      name: string;
      oem?: string;
    };
  };
  currentVehicle?: {
    id: string;
    registrationNumber: string;
    model?: {
      name: string;
      oem?: string;
    };
  };
  swapHistory?: BatterySwapHistoryRecord[];
}

export interface BatterySwapHistoryRecord {
  id: string;
  batteryId: string;
  fromVehicleId?: string;
  toVehicleId?: string;
  swapDate: string;
  swapReason?: string;
  performedBy?: string;
  fromSlotPosition?: number;
  toSlotPosition?: number;
  remarks?: string;
  fromVehicle?: {
    id: string;
    registrationNumber: string;
    model?: { name: string };
  };
  toVehicle?: {
    id: string;
    registrationNumber: string;
    model?: { name: string };
  };
}

export interface CreateBatteryDTO {
  batteryNumber: string;
  parentVehicleId: string;
  currentVehicleId?: string;
  manufacturer?: string;
  batteryType?: string;
  capacity?: number;
  voltage?: number;
  healthPercentage?: number;
  cycleCount?: number;
  lastChargeDate?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status?: BatteryStatus;
  slotPosition?: number;
  notes?: string;
}

export interface UpdateBatteryDTO {
  batteryNumber?: string;
  manufacturer?: string;
  batteryType?: string;
  capacity?: number;
  voltage?: number;
  healthPercentage?: number;
  cycleCount?: number;
  lastChargeDate?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status?: BatteryStatus;
  slotPosition?: number;
  notes?: string;
}

export interface SwapBatteryDTO {
  batteryId: string;
  fromVehicleId?: string;
  toVehicleId?: string;
  swapReason?: string;
  performedBy?: string;
  fromSlotPosition?: number;
  toSlotPosition?: number;
  remarks?: string;
}

export interface BatteryHealthStats {
  total: number;
  excellent: number;
  good: number;
  fair: number;
  poor: number;
  averageHealth: number;
  averageCycles: number;
  active: number;
  inStorage: number;
  underRepair: number;
  damaged: number;
  retired: number;
}

// Configure axios instance
const batteryApi = axios.create({
  baseURL: `${BATTERY_API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
batteryApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
batteryApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Battery API Error:", error);
    if (error.response?.status === 401) {
      // Handle unauthorized
      const unauthorizedCounter = parseInt(
        sessionStorage.getItem("battery_api_401_count") || "0"
      );
      sessionStorage.setItem(
        "battery_api_401_count",
        (unauthorizedCounter + 1).toString()
      );

      if (unauthorizedCounter >= 5) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        alert("Your session has expired. You will be redirected to login.");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Battery Service Methods
export const batteryService = {
  /**
   * Create new battery
   */
  async createBattery(data: CreateBatteryDTO): Promise<Battery> {
    console.log("🔋 Battery Service: Creating battery with data:", data);
    const response = await batteryApi.post("/", data);
    console.log("📦 Battery Service: Received response:", {
      status: response.status,
      statusText: response.statusText,
      fullResponse: response.data,
      nestedData: response.data?.data,
    });
    if (!response.data?.data) {
      console.warn(
        "⚠️ Battery Service: Response missing nested data property. Full response:",
        response.data
      );
      // If data is missing, return the full response
      return response.data as any;
    }
    return response.data.data;
  },

  /**
   * Get battery by ID
   */
  async getBatteryById(id: string): Promise<Battery> {
    const response = await batteryApi.get(`/${id}`);
    return response.data.data;
  },

  /**
   * Get battery by battery number
   */
  async getBatteryByNumber(batteryNumber: string): Promise<Battery> {
    const response = await batteryApi.get(`/number/${batteryNumber}`);
    return response.data.data;
  },

  /**
   * Get all batteries with optional filters
   */
  async getAllBatteries(filters?: {
    status?: BatteryStatus;
    currentVehicleId?: string;
    parentVehicleId?: string;
  }): Promise<Battery[]> {
    const response = await batteryApi.get("/", { params: filters });
    return response.data.data;
  },

  /**
   * Get batteries currently in a vehicle
   */
  async getBatteriesByVehicle(vehicleId: string): Promise<Battery[]> {
    const response = await batteryApi.get(`/vehicle/${vehicleId}`);
    return response.data.data;
  },

  /**
   * Get batteries by parent vehicle (originally came with)
   */
  async getBatteriesByParentVehicle(
    parentVehicleId: string
  ): Promise<Battery[]> {
    const response = await batteryApi.get(`/parent/${parentVehicleId}`);
    return response.data.data;
  },

  /**
   * Get available batteries for swapping
   */
  async getAvailableBatteries(
    batteryType?: string,
    capacity?: number
  ): Promise<Battery[]> {
    const response = await batteryApi.get("/available", {
      params: { batteryType, capacity },
    });
    return response.data.data;
  },

  /**
   * Update battery
   */
  async updateBattery(id: string, data: UpdateBatteryDTO): Promise<Battery> {
    const response = await batteryApi.put(`/${id}`, data);
    return response.data.data;
  },

  /**
   * Swap battery between vehicles or to/from storage
   */
  async swapBattery(data: SwapBatteryDTO): Promise<{
    battery: Battery;
    swapHistory: BatterySwapHistoryRecord;
  }> {
    const response = await batteryApi.post("/swap", data);
    return response.data.data;
  },

  /**
   * Get swap history for a battery
   */
  async getBatterySwapHistory(
    batteryId: string
  ): Promise<BatterySwapHistoryRecord[]> {
    const response = await batteryApi.get(`/${batteryId}/history`);
    return response.data.data;
  },

  /**
   * Get swap history for a vehicle
   */
  async getVehicleSwapHistory(
    vehicleId: string
  ): Promise<BatterySwapHistoryRecord[]> {
    const response = await batteryApi.get(
      `/vehicles/${vehicleId}/battery-swap-history`
    );
    return response.data.data;
  },

  /**
   * Get battery health statistics
   */
  async getBatteryHealthStats(): Promise<BatteryHealthStats> {
    const response = await batteryApi.get("/stats/health");
    return response.data.data;
  },

  /**
   * Soft delete battery (retire)
   */
  async deleteBattery(id: string): Promise<Battery> {
    const response = await batteryApi.delete(`/${id}`);
    return response.data.data;
  },

  /**
   * Permanently delete battery
   */
  async permanentlyDeleteBattery(id: string): Promise<void> {
    await batteryApi.delete(`/${id}/permanent`);
  },

  
  /**
   * Update battery status
   */
  async updateBatteryStatus(batteryNumber: string, status: BatteryStatus): Promise<Battery> {
    const response = await batteryApi.post("/update-status", {
      batteryNumber,
      status,
    });
    return response.data.data;
  },

};

export default batteryService;
